import React from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, TextField, Typography, Box
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

const OrderItemsTable = ({ items, onUpdateItem, onRemoveItem }) => {
    if (!items || items.length === 0) {
        return (
            <Paper sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, bgcolor: '#f8f9fa' }}>
                <Typography color="textSecondary">No hay productos en el pedido</Typography>
            </Paper>
        );
    }

    return (
        <>
            {/* Desktop / Tablet: Tabla */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e7edf3', display: { xs: 'none', md: 'block' } }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                        <TableRow>
                            <TableCell>Producto</TableCell>
                            <TableCell align="right">Cantidad</TableCell>
                            <TableCell align="right">Precio Unit.</TableCell>
                            <TableCell align="right">Peso Real (kg)</TableCell>
                            <TableCell align="right">Subtotal</TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell component="th" scope="row">
                                    <Box>
                                        <Typography variant="body2" fontWeight="600">{item.product_name || item.product?.name}</Typography>
                                        <Typography variant="caption" color="textSecondary">{item.product_category || item.product?.category_name}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="right">
                                    <TextField
                                        type="number"
                                        size="small"
                                        defaultValue={item.quantity}
                                        onBlur={(e) => {
                                            const val = parseFloat(e.target.value);
                                            if (val !== parseFloat(item.quantity) && val > 0) {
                                                onUpdateItem(item.id, { quantity: val });
                                            }
                                        }}
                                        inputProps={{ min: 0, step: 0.1, style: { textAlign: 'right', padding: '4px 8px' } }}
                                        sx={{ width: 80 }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body2">
                                        ${parseFloat(item.unit_price).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <TextField
                                        type="number"
                                        size="small"
                                        defaultValue={item.actual_weight || ''}
                                        placeholder="-"
                                        onBlur={(e) => {
                                            const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                            if (val !== item.actual_weight) {
                                                onUpdateItem(item.id, { actual_weight: val });
                                            }
                                        }}
                                        inputProps={{ min: 0, step: 0.001, style: { textAlign: 'right', padding: '4px 8px' } }}
                                        sx={{ width: 90 }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body2" fontWeight="600">
                                        ${parseFloat(item.subtotal).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" color="error" onClick={() => onRemoveItem(item.id)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Mobile: Tarjetas apiladas (sin scroll horizontal) */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
                {items.map((item) => (
                    <Paper key={item.id} elevation={0} sx={{ border: '1px solid #e7edf3', borderRadius: '0.5rem', p: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight="700" sx={{ lineHeight: 1.2 }}>
                                    {item.product_name || item.product?.name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {item.product_category || item.product?.category_name}
                                </Typography>
                            </Box>
                            <IconButton size="small" color="error" onClick={() => onRemoveItem(item.id)} sx={{ flexShrink: 0, mt: -0.5, mr: -0.5 }}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                            <TextField
                                label="Cantidad"
                                type="number"
                                size="small"
                                fullWidth
                                defaultValue={item.quantity}
                                onBlur={(e) => {
                                    const val = parseFloat(e.target.value);
                                    if (val !== parseFloat(item.quantity) && val > 0) {
                                        onUpdateItem(item.id, { quantity: val });
                                    }
                                }}
                                inputProps={{ min: 0, step: 0.1 }}
                            />
                            <Box>
                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block' }}>
                                    Precio Unit.
                                </Typography>
                                <Typography variant="body2" fontWeight="600">
                                    ${parseFloat(item.unit_price).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Subtotal</Typography>
                            <Typography variant="body2" fontWeight="800">
                                ${parseFloat(item.subtotal).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                            </Typography>
                        </Box>
                    </Paper>
                ))}
            </Box>
        </>
    );
};

export default OrderItemsTable;
